// One WebGL context draws every avatar on the page: each is rendered into a
// corner of a shared canvas, then copied into its own 2D canvas. Make one
// renderer per page (browsers cap live WebGL contexts at around sixteen).
import { drawFullscreen, fullscreenProgram, uniforms } from '../../gl.ts';
import { partIndices, type AvatarConfig } from './catalog.ts';
import { avatarFrag } from './shader.ts';

export interface AvatarRendererOptions {
  // Transparent output (premultiplied, no background or vignette), for drawing
  // avatars over other content. Off by default: an opaque dark tile.
  alpha?: boolean;
}

export class AvatarRenderer {
  private readonly canvas = document.createElement('canvas');
  private readonly gl: WebGLRenderingContext;
  private readonly alpha: boolean;
  private readonly u: Record<'uRes' | 'uTime' | 'uIdn' | 'uSty', WebGLUniformLocation | null>;

  constructor(options: AvatarRendererOptions = {}) {
    this.alpha = options.alpha ?? false;
    const gl = this.canvas.getContext('webgl', { antialias: false, alpha: this.alpha, premultipliedAlpha: true, preserveDrawingBuffer: true });
    if (!gl) throw new Error('WebGL is unavailable');
    this.gl = gl;
    this.u = uniforms(gl, fullscreenProgram(gl, avatarFrag(this.alpha)), ['uRes', 'uTime', 'uIdn', 'uSty'] as const);
  }

  // Draw one frame of `config` at `time` seconds into target, at its pixel size.
  draw(target: HTMLCanvasElement, config: AvatarConfig, time: number): void {
    const { gl, canvas, u } = this;
    const w = target.width, h = target.height;
    if (canvas.width < w || canvas.height < h) {
      canvas.width = Math.max(canvas.width, w);
      canvas.height = Math.max(canvas.height, h);
    }
    const { identity, style } = partIndices(config);
    gl.viewport(0, 0, w, h);
    gl.uniform2f(u.uRes, w, h);
    gl.uniform1f(u.uTime, time);
    gl.uniform3i(u.uIdn, ...identity);
    gl.uniform3i(u.uSty, ...style);
    drawFullscreen(gl);
    const ctx = target.getContext('2d');
    if (!ctx) return;
    if (this.alpha) ctx.clearRect(0, 0, w, h);
    // The viewport is the bottom-left corner; drawImage counts from the top.
    ctx.drawImage(canvas, 0, canvas.height - h, w, h, 0, 0, w, h);
  }
}
